import { useStoreProfiles } from '../store/useStoreProfiles'
import { useDatabase } from '../composables/database'
import { useStoreRecaps } from '../store/useStoreRecaps'
import { storeToRefs } from 'pinia'

type StoreRecapDefinitionsModelReturn = {
  getAllStoreRecapDefinitions: () => Promise<DatabaseStoreRecapDefinition[]>
  getStoreRecapDefinition: (
    recap_id: string
  ) => Promise<DatabaseStoreRecapDefinition[] | void | null>
  getRecapDefinitionsForStore: (store_id: string) => Promise<DatabaseStoreRecapDefinition[]>
  writeStoreRecapDefinitions: (
    definitions: any,
    store_mappings: { [key: string]: string[] }
  ) => Promise<boolean>
  removeStaleStoreRecapDefinitions: () => Promise<void>
  clearStoreRecaps: () => Promise<void>
  /** Returns [{recap_id: int, hash: string}] */
  getStoreRecapDefinitionHashes: () => Promise<{ [key: string]: string }>
}

export type DatabaseStoreRecapDefinition = {
  recap_id: string
  recap_definition: string
  store_ids: string
}

/**
 * Interacts with the recap definitions table (mobile only)
 * @returns {RecapResponsesModelReturn}
 */
export const StoreRecapDefinitionsModel = (): StoreRecapDefinitionsModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useStoreRecaps())

  async function getAllStoreRecapDefinitions(): Promise<DatabaseStoreRecapDefinition[]> {
    const sql = 'SELECT * FROM store_recap_definitions'
    const definitions =
      ((await query(sql).catch((error) =>
        console.log(`Error fetching recap response: ${error}`)
      )) as unknown as DatabaseStoreRecapDefinition[]) || []

    return definitions
  }

  async function getStoreRecapDefinition(
    recap_id: string
  ): Promise<DatabaseStoreRecapDefinition[] | void | null> {
    const sql = `
      SELECT recap_definition
      FROM store_recap_definitions
      WHERE recap_id = ${recap_id}
      LIMIT 1;
    `

    return await query(sql)
  }

  async function getRecapDefinitionsForStore(
    store_id: string
  ): Promise<DatabaseStoreRecapDefinition[]> {
    const possibleDefinitions = await getAllStoreRecapDefinitions()
    if (possibleDefinitions && possibleDefinitions.length) {
      return possibleDefinitions.filter((def) => {
        const storeIds = def.store_ids.split(',')
        if (storeIds.includes(store_id)) {
          return JSON.parse(def.recap_definition)
        }
      })
    }
    return []
  }

  async function writeStoreRecapDefinitions(
    recaps: any,
    store_mappings: { [key: string]: string[] }
  ): Promise<boolean> {
    const failedInserts: any[] = []
    const recapKeys = Object.keys(recaps)

    for (const key of recapKeys) {
      const recap = recaps[key as any] as any
      const mappings = store_mappings[key]
      if (mappings && mappings.length) {
        const existingRecapSql = `SELECT * FROM store_recap_definitions WHERE recap_id = ${recap.id}`
        const existingRecaps = await query(existingRecapSql).catch((error) =>
          console.error(`Error fetching existing recap definitions: ${error}`)
        )
        if (existingRecaps && existingRecaps.length) {
          const sql = `UPDATE store_recap_definitions 
                       SET recap_definition = ?, store_ids = ?, hash = ?
                       WHERE recap_id = ?`
          await run(sql, [JSON.stringify(recap), mappings.join(','), recap.hash, recap.id]).catch(
            (error) => {
              console.log(`Error updating store recap definition: ${error}`)
              failedInserts.push(recap)
            }
          )
        } else {
          const sql = `
            INSERT INTO store_recap_definitions ('recap_id', 'recap_definition', 'store_ids', 'hash')
            VALUES (?, ?, ?, ?)
          `
          await run(sql, [recap.id, JSON.stringify(recap), mappings.join(','), recap.hash]).catch(
            (error) => {
              console.log(`Error writing store recap definition: ${error}`)
              failedInserts.push(recap)
            }
          )
        }
      }
    }
    return !(failedInserts && failedInserts.length)
  }

  // Remove any old recap definitions
  async function removeStaleStoreRecapDefinitions() {
    const { getAllStoreData } = useStoreProfiles()
    if (!getAllStoreData || !getAllStoreData.length) {
      // There are no stores, so we just want to blanket delete all store recap definition data
      const sql = 'DELETE FROM store_recap_definitions'
      await run(sql, []).catch((error) =>
        console.error(`Error deleting stale store recap definition data: ${error}`)
      )
    } else {
      // We have task data, go through each of the recaps and find what tasks they're associated with
      // If we find a recap definition that isn't associated to any of the tasks, delete it
      const storeIds = getAllStoreData.map((store: any) => store.store_id)
      const recapsSql = 'SELECT * FROM store_recap_definitions'
      const recaps = await query(recapsSql).catch((error) =>
        console.error(`Error fetching all store recap definitions: ${error}`)
      )
      if (recaps && recaps.length) {
        recaps.forEach(async (recap) => {
          const parsedStoreIds = recap.store_ids.split(',')
          if (!storeIds.some((id) => parsedStoreIds.includes(id))) {
            const sql = 'DELETE FROM store_recap_definitions WHERE recap_id = ?'
            await run(sql, [recap.recap_id]).catch((error) =>
              console.error(`Error deleting store recap definition: ${error}`)
            )
          }
        })
      }
    }
  }

  async function getStoreRecapDefinitionHashes() {
    const sql = 'SELECT recap_id, hash from store_recap_definitions'
    const hashes = await query(sql)
    if (hashes) {
      const returnObj: { [key: string]: string } = {}
      for (const row of hashes) {
        returnObj[row.recap_id as any] = row.hash
      }
      return returnObj
    }
    return {}
  }

  const clearStoreRecaps = async () => {
    const sql = 'DELETE FROM store_recap_definitions'
    await run(sql, [])
      .catch((error) => console.log(`Error deleting store recap definitions: ${error}`))
      .then(() => {
        const keys = Object.keys(recapStore.recaps.value)
        for (const key of keys) {
          delete recapStore.recaps.value[key]
        }
      })
  }
  return {
    getAllStoreRecapDefinitions,
    getStoreRecapDefinition,
    getRecapDefinitionsForStore,
    writeStoreRecapDefinitions,
    removeStaleStoreRecapDefinitions,
    clearStoreRecaps,
    getStoreRecapDefinitionHashes,
  }
}
